/*
 * Design note for this page:
 * Cybernetic Editorial Observatory applied to security and resilience.
 * Present a serious but measured posture: protect owner controls, customer data,
 * reports, and service continuity without making unrealistic absolute-security claims.
 */

import PolicyLayout from "@/components/PolicyLayout";
import { FileLock2, ShieldCheck, Siren } from "lucide-react";

const cards = [
  {
    title: "Owner-access protection",
    body: "Owner-level settings, billing administration, and sensitive operational surfaces should be protected by strong passwords, limited access, verification checks, and monitored administrative handling.",
    icon: ShieldCheck,
  },
  {
    title: "Anti-abuse posture",
    body: "Command Secrets may use layered controls intended to reduce scraping, cloning, unauthorized automation, hostile probing, and technical interference against its website, reports, and app surfaces.",
    icon: Siren,
  },
  {
    title: "Protected outputs",
    body: "Generated reports and premium analytical materials may be watermarked, access-controlled, and delivery-tracked to discourage copying, resale, and misuse of proprietary intellectual property.",
    icon: FileLock2,
  },
];

const sections = [
  {
    id: "access-control",
    title: "Owner access, account control, and administrative boundaries",
    intro:
      "Administrative control over Command Secrets should remain limited, intentional, and auditable rather than casually shared.",
    paragraphs: [
      "Owner and administrator access should be protected by strong unique passwords, controlled credential handling, device hygiene, and reviewable administrative processes. Sensitive actions such as changing billing rules, modifying protected content, exposing private data, or altering deployment-critical settings should not be available through weak or publicly exposed pathways.",
      "Where technically available, Command Secrets may also use additional safeguards such as role separation, login monitoring, recovery controls, session review, and step-up verification for especially sensitive actions. These controls help reduce the risk of fake intrusion attempts, unauthorized admin impersonation, or accidental exposure of customer information.",
      "Customers should also help protect their own accounts by using secure passwords, limiting credential sharing, and reporting suspicious login or billing activity promptly through the support path.",
    ],
  },
  {
    id: "anti-abuse",
    title: "Anti-scraping, anti-cloning, and intervention resistance",
    intro:
      "Command Secrets is a digital service with protected commercial logic, so defensive measures may be used against technical abuse and unlawful copying.",
    paragraphs: [
      "Reasonable security and platform-protection measures may include logging, rate limiting, controlled asset exposure, access checks, download protection, bot filtering, anomaly review, and technical controls intended to reduce hostile crawling, developer-tool abuse, cloning attempts, scripted extraction, and unauthorized intervention against report or pricing surfaces.",
      "These measures are used to protect customers, infrastructure, and proprietary methodology. They should not be interpreted as a promise that abuse can never occur, but they do signal that Command Secrets treats intellectual-property protection and service integrity as operational requirements rather than optional polish.",
      "If the service detects suspicious behavior, it may restrict access, require additional verification, preserve event logs, or suspend certain functionality while the event is reviewed. Such action may be taken to protect the platform, customers, vendors, and legal rights of the business.",
    ],
  },
  {
    id: "reports",
    title: "PDF watermarking, report protection, and controlled distribution",
    intro:
      "Premium analytical outputs are designed to remain attributable to their lawful source.",
    paragraphs: [
      "Where PDF or similar report files are delivered, Command Secrets may apply watermarking, account-linked identifiers, controlled download paths, or related protective notices to preserve authorship and reduce unauthorized recirculation. These measures are intended to protect both commercial value and evidentiary clarity around what was delivered, to whom, and through which service path.",
      "Customers should not remove, obscure, falsify, crop out, or misrepresent such identifiers. They should also avoid reposting protected reports publicly or sending them to third parties unless they accept responsibility for the consequences of that onward sharing.",
      "The public website describes the outputs at a high level, but it does not disclose full proprietary formulas, complete scoring order, or the full internal architecture used to generate individualized analytical results.",
    ],
  },
  {
    id: "resilience",
    title: "Anti-crash, broken-link, and continuity measures",
    intro:
      "Security also includes resilience: a service that breaks silently loses trust even when no attacker is involved.",
    paragraphs: [
      "Command Secrets aims to maintain a stable customer journey by reviewing broken links, failed buttons, report-delivery faults, and crash patterns as operational defects that require correction. Preventive measures may include monitored logging, route checks, controlled release changes, support escalation paths, and issue triage when an important customer flow becomes unstable.",
      "Because modern websites depend on browsers, devices, content delivery, third-party infrastructure, and evolving software versions, temporary disruptions can still occur. The service therefore treats support reporting and fault visibility as part of reliability protection, not as an afterthought.",
      "If you discover a serious security issue, suspected unauthorized access, report leak, or clone-like misuse of Command Secrets branding or materials, please report it promptly through the support path so it can be reviewed as a security and integrity matter.",
    ],
  },
];

const relatedLinks = [
  {
    label: "Support",
    href: "/support",
    description: "Report suspicious activity, broken links, crashes, delivery failures, or billing issues through the public support path.",
  },
  {
    label: "Privacy notice",
    href: "/legal/privacy",
    description: "See how logging, access control, retention, and customer data handling fit into the service's privacy posture.",
  },
  {
    label: "Cancellation terms",
    href: "/legal/cancellation",
    description: "Understand how protected reports and rendered analysis are treated after billing, delivery, and cancellation requests.",
  },
  {
    label: "Disclaimer",
    href: "/legal/disclaimer",
    description: "Review the legal boundaries around examples, estimated data, and proprietary automated analysis language.",
  },
];

export default function Security() {
  return (
    <PolicyLayout
      eyebrow="Security posture"
      title="Protect the customer surface, the owner controls, and the proprietary report layer."
      intro="This page describes the public-facing security and resilience posture for Command Secrets, including owner access expectations, anti-abuse measures, report protection, and the handling of broken links, crash events, and suspicious technical interference."
      highlight="Security is treated as service architecture, not as a decorative claim."
      cards={cards}
      sections={sections}
      relatedLinks={relatedLinks}
    />
  );
}
